// Lightweight audit logger. Replaces legacy assets/js/admin/audit.js.
// Writes to `audit_logs` (insert-allowed for any authenticated user via RLS)
// AND falls back to console when no remote DB is wired (local mode).

import type { SupaWrapper } from './supabase-client';
import type { AuthApi } from './auth';

export interface AuditApi {
  logEvent(actionType: string, details?: Record<string, unknown>): Promise<void>;
  logCreate(table: string, recordId: string, after: Record<string, unknown>): Promise<void>;
  logUpdate(
    table: string,
    recordId: string,
    before: Record<string, unknown>,
    after: Record<string, unknown>,
  ): Promise<void>;
  logDelete(table: string, recordId: string, before: Record<string, unknown>): Promise<void>;
  getRecentLogs(limit?: number): Promise<unknown[]>;
}

export function createAudit(supa: SupaWrapper, auth: AuthApi): AuditApi {
  async function insertLog(row: Record<string, unknown>): Promise<void> {
    if (!supa.client) return;
    const { error } = await supa.client.from('audit_logs').insert({
      ...row,
      session_id: auth.getSessionId(),
    });
    if (error) console.warn('[audit] insert failed:', supa.explainError(error));
  }

  return {
    async logEvent(actionType, details) {
      await insertLog({ action_type: actionType, details });
    },
    async logCreate(table, recordId, after) {
      await insertLog({
        action_type: 'create',
        table_name: table,
        record_id: recordId,
        new_value: after,
      });
    },
    async logUpdate(table, recordId, before, after) {
      await insertLog({
        action_type: 'update',
        table_name: table,
        record_id: recordId,
        old_value: before,
        new_value: after,
      });
    },
    async logDelete(table, recordId, before) {
      await insertLog({
        action_type: 'delete',
        table_name: table,
        record_id: recordId,
        old_value: before,
      });
    },
    async getRecentLogs(limit = 100) {
      if (!supa.client) return [];
      const { data, error } = await supa.client
        .from('audit_logs')
        .select('*')
        .order('ts', { ascending: false })
        .limit(limit);
      if (error) {
        console.warn('[audit] getRecentLogs:', supa.explainError(error));
        return [];
      }
      return data ?? [];
    },
  };
}
